import { useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import ky from 'ky';
import ReCAPTCHA from 'react-google-recaptcha';

// Components
import Button from 'epn-ui/umd/dist/components/Button';
import Form from 'epn-ui/umd/dist/components/Form';
import InputText from 'epn-ui/umd/dist/components/InputText';
import notification from 'epn-ui/umd/dist/components/Notification';
import Select from 'epn-ui/umd/dist/components/Select';

// Helpers
import apiHooks from '@apiHooks';
import useI18n from '@i18n';
import { WEBSITES_PATTERN } from '@utils/regex';

import { IGetCountriesDataFormatted, IResponse } from '@api/types';

// Styles
import styles from './Form.module.scss';

interface IFormData {
    name: string;
    company: string;
    websites: string;
    email: string;
    phone: string;
    address: string;
    geoTargeting: string;
    info?: string;
}

// TODO вынести в общее
const ADVERT_APP_CAPTCHA_KEY = '6LdLYTEUAAAAAGuJ4UOnX_nQ6H1MBeBH6A8otJvy';

const DEFAULT_COUNTRY = {
    epn: {
        ru: 'Российская Федерация',
        en: 'Russian Federation',
    },
    epncpa: {
        ru: 'United Kingdom',
        en: 'United Kingdom',
    },
}[process.env.NEXT_PUBLIC_PROJECT_NAME];

const { useCountries } = apiHooks;

const ApplicationForm: React.FC = () => {
    const { t, locale } = useI18n('Cpa');
    const { t: tCommon } = useI18n('Common');

    const [loading, setLoading] = useState<boolean>(false);
    const [recaptcha, setRecaptcha] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [form] = Form.useForm();

    const { data: countries, isLoading: isLoadingCountries } = useCountries<
        IGetCountriesDataFormatted[]
    >({ optionalAuth: true, dependencies: [locale] });

    useEffect(() => {
        form.setFieldsValue({ geoTargeting: DEFAULT_COUNTRY[locale] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale]);

    /**
     * @returns {void}
     */
    const resetCaptcha = (): void => {
        recaptchaRef?.current?.reset();
        setRecaptcha(null);
    };

    /**
     * Данная форма отправляется вызовом Ky т.к. базовый url отличается от основного API
     * @param {IFormData} formData
     * @returns {Promise<void>}
     */
    const onSubmitHandler = async (formData: IFormData): Promise<void> => {
        setLoading(true);

        try {
            const response = await ky.post(
                `${process.env.NEXT_PUBLIC_INSTANCE_PREFIX}/affiliate/appliedForOffer`,
                {
                    json: {
                        name: formData.name,
                        company: formData.company,
                        websites: [formData.websites],
                        email: formData.email,
                        phone: formData.phone,
                        address: formData.address,
                        geoTargeting: formData.geoTargeting,
                        info: formData.info,
                        recaptcha,
                    },
                },
            );

            const parsedResp = await response.json<IResponse<unknown>>();

            if (parsedResp.errors && Array.isArray(parsedResp.errors)) {
                parsedResp.errors.forEach(item => {
                    notification.error({ message: item.title || '' });
                });
                return;
            }

            notification.success({
                message: t('Application successfully sent'),
            });
            form.resetFields();
            resetCaptcha();
        } catch (err) {
            notification.error({ message: t('Failed to send application') });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className={styles.Form}>
            <div className={styles.landingContainer}>
                <div className={styles.Form__content}>
                    <Form
                        form={form}
                        name="application-form"
                        onFinish={onSubmitHandler}
                        scrollToFirstError
                    >
                        <Form.Item
                            name="name"
                            label={t('Your name')}
                            labelAlign="left"
                            required
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    message: tCommon('Required field'),
                                },
                            ]}
                        >
                            <InputText placeholder={t('Enter your name (full name)')} />
                        </Form.Item>

                        <Form.Item
                            name="company"
                            label={t('Company name')}
                            labelAlign="left"
                            required
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    message: tCommon('Required field'),
                                },
                            ]}
                        >
                            <InputText placeholder={t('Enter the name of your company')} />
                        </Form.Item>

                        <Form.Item
                            name="websites"
                            label={t('Website')}
                            labelAlign="left"
                            required
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    message: tCommon('Required field'),
                                },
                                {
                                    pattern: new RegExp(WEBSITES_PATTERN),
                                    message: t(
                                        'Invalid link format. Only links of the following formats are allowed ... Please correct it and try again',
                                    ),
                                },
                            ]}
                        >
                            <InputText placeholder="https://example.com" />
                        </Form.Item>

                        <div className={styles.group}>
                            <Form.Item
                                name="email"
                                label={t('E-mail')}
                                labelAlign="left"
                                required
                                rules={[
                                    {
                                        type: 'email',
                                        message: t('The input is not valid email'),
                                        validateTrigger: 'onBlur',
                                    },
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: tCommon('Required field'),
                                    },
                                ]}
                            >
                                <InputText placeholder="yourname@mailbox.com" />
                            </Form.Item>

                            <Form.Item
                                name="phone"
                                label={t('Phone')}
                                labelAlign="left"
                                required
                                rules={[
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: tCommon('Required field'),
                                    },
                                ]}
                            >
                                <InputText
                                    placeholder="+7 00 00 00 00 00"
                                    mask="{+}{0}(000)000-00-00[000000000]"
                                />
                            </Form.Item>
                        </div>

                        <div className={styles.group}>
                            <Form.Item
                                name="address"
                                label={t('ad_ap_land_Address')}
                                labelAlign="left"
                                required
                                rules={[
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: tCommon('Required field'),
                                    },
                                ]}
                            >
                                <InputText placeholder={t("Your company's address")} />
                            </Form.Item>

                            <Form.Item
                                name="geoTargeting"
                                label={t('Geo-targeting')}
                                labelAlign="left"
                                required
                                rules={[
                                    {
                                        required: true,
                                        whitespace: true,
                                        message: tCommon('Required field'),
                                    },
                                ]}
                                initialValue={DEFAULT_COUNTRY[locale]}
                            >
                                <Select
                                    options={countries}
                                    loading={isLoadingCountries}
                                    fieldNames={{
                                        label: 'label',
                                        value: 'label',
                                    }}
                                />
                            </Form.Item>
                        </div>

                        <Form.Item name="info" label={t('Additionally')} labelAlign="left">
                            <InputText.TextArea
                                autoSize
                                placeholder={t('Additionally information')}
                            />
                        </Form.Item>

                        <Form.Item
                            className={clsx(styles.Form__captcha, styles.Form__biggerMargin)}
                        >
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={ADVERT_APP_CAPTCHA_KEY}
                                onChange={setRecaptcha}
                                onExpired={resetCaptcha}
                                hl={locale}
                            />
                        </Form.Item>

                        <Form.Item className={styles.Form__biggerMargin}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                disabled={!recaptcha}
                                className={styles.Form__applicationBtn}
                                size="large"
                            >
                                {t('Send application')}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </section>
    );
};

export default ApplicationForm;
